import { useNavigate, useLocation, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';

import TwoLevelTabNavigation from 'components/Navigation/TwoLevelTabNavigation';
import { LightTooltip } from 'components/Common/LightTooltip';
import { makeStyles } from '@mui/styles';
import { ToggleType } from 'types/Financials';
import { GlobalState } from 'reducers/stateTypes';
import featureService from 'services/featureService';
import { TOPBAR_HEIGHT } from 'services/constants';
import { setAccountCaseInformation, setCaseId } from 'reducers/caseReducer';
import investmentProfileService from 'services/investmentProfileService';
import { TabContext } from '@mui/lab';
import { AppBar, Box, Button, CircularProgress, Grid, Stack, SvgIcon, Tooltip, Typography } from '@mui/material';
import { openModal } from 'reducers/modalReducer';
import { ReactComponent as HistoryIcon } from 'assets/icons/history.svg';
import { findConfigFlagByCode, INVESTMENT_PROFILE_ENABLED_FLAG, NNM_ENABLED_FLAG } from 'utils/configFlagUtil';
import { getIFrame, openInSameTab, requestHostNavigation } from 'utils/browserUtil';
import EmbeddedIFrame from 'views/Frames/EmbeddedIFrame';
import { routeList } from 'Routes';
import { updateNotifications } from 'reducers/notificationReducer';
import { BrTypes } from 'types/BrTypes';
import { partnerTabStyles } from 'views/PartnerTabs/LegalPartnerFormTabs';
import AccountsMoreButton from 'views/BrTabs/BrTabsMoreButton';
import { setBrTab } from 'reducers/brTabsReducer';
import BrTabsFrames from 'views/BrTabs/BrTabsFrames';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { ReactComponent as Warning } from 'assets/icons/Warning16.svg';
import { JBButton, JBColors, JBIcon, ZfAccessDenied } from '@bjb-gcrm/gcrm-frontend-components';
import { useFetchBrLock } from 'hooks/queries/useFetchBrLock';
import { useFetchBlockingInstructions } from 'hooks/queries/useFetchBlockingInstructions';
import { LockTypes } from 'types/PartnerLockTypes';
import { BlockingInstructionTag } from 'components/Common/BlockingInstructionTag';
import { getBrMasterNumberForDisplay, isNotNullOrUndefined, isNullOrUndefined } from 'utils/utils';
import { KycLockMessage } from 'components/Common/KycLockMessage';
import { usePostMessageToIFrame } from 'hooks/usePostMessageToIFrame';
import { ClmPortalIncomingPostMessageTypes, ClmPortalOutgoingPostMessageTypes } from 'utils/postMessageUtil';
import { useDetectMultipleTabs } from 'hooks/useDetectMultipleTabs';
import { MultiTabWarning } from 'components/Common/MultiTabWarning';
import PageTitleToken from 'components/Common/PageTitleToken';
import { useHost } from 'contexts/Host';
import { useFetchBr } from 'hooks/queries/useFetchBr';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { resolveNavigationWizardMode } from 'views/BrTabs/utils/navigationWizardMode';

export enum BrTabItems {
  PRODUCTS_AND_SERVICES = 'products-and-services',
  PORTFOLIOS = 'portfolios',
  DOCUMENT_OVERVIEW = 'document-overview',
  DISTRIBUTION = 'distribution',
  DOC_COLLECTION = 'doc-collection',
  FINANCIALS = 'financials',
  INVESTMENT_PROFILE = 'investment-profile',
  BR_RELATIONS = 'br-relations',
  BR_SERVICING = 'br-servicing-roles',
  BR_DELIVERY_INSTRUCTIONS = 'br-delivery-instructions',
  BLOCKING_INSTRUCTIONS = 'blocking-instructions',
  BR_PURPOSE = 'br-purpose',
  CORE_DATA = 'core-data',
  BR_CONTACT_NOTES = 'br-notes',
  REPORTING = 'reporting',
  RISK_CONSIDERATIONS = 'risk-considerations',
  CLIENT_REVIEW = 'client-review',
  COUNTRY_RISK_WAIVERS = 'country-risk-waivers',
}

export const tabWarningStyles = makeStyles(() => ({
  tabIconWrapper: {
    flexDirection: 'row-reverse',
  },
  tabIconLabelContainer: {
    width: 'auto',
    padding: 0,
    minHeight: 'unset',
    marginLeft: '25px',
  },
  warningIconPlacement: {
    marginBottom: '2px',
  },
}));

export type CIPState = 'SHOW' | 'NOT_ALLOWED' | 'MISSING';

export type CIPInfo = {
  state: CIPState;
};

export const enum BrFormCategories {
  GENERAL = 'general',
  PROFILE = 'profile',
  RELATIONSHIPS = 'relationships',
  INTERACTIONS = 'interactions',
  DOCUMENTS = 'documents',
  PRODUCTS_AND_SERVICES = 'products-and-services',
  REPORTING = 'reporting',
}

const BrTabs = () => {
  const classes = tabWarningStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const { isWena, shadowRoot } = useHost();

  const { brId: brIdFromUrl, tab: selectedTabFromUrl } = useParams<{ brId: string; tab: string }>();
  const brIdFromStore = useSelector((state: GlobalState) => state.caseInformation.accountInformation?.brUid);
  const hasMultipleTabs = useDetectMultipleTabs(brIdFromUrl);
  const queryParams = new URLSearchParams(useLocation()?.search);
  const urlSearchDocumentId = queryParams.get('documentId');
  const distributionRequestIdFromParam = queryParams.get('distributionRequestId');
  const brSource = queryParams.get('brSource');
  const serviceAgreementUid = queryParams.get('serviceAgreementUid');
  const serviceRoleUid = queryParams.get('serviceRoleUid');
  const { data: brLockData, error: brLockError } = useFetchBrLock(brIdFromUrl);
  const isBrLocked = isNotNullOrUndefined(brLockData?.find((lockStatus) => lockStatus.lockType === LockTypes.KYC));
  const [pcoUrl, setPcoUrl] = React.useState<string>('');
  const selectedTab = useSelector((state: GlobalState) => state.brTabs.tab);
  const userId = useSelector((state: GlobalState) => state?.user?.userDetails?.username);
  const [isBrSaved, setIsBrSaved] = React.useState(false);
  const [toggleFinancialsTab, setToggleFinancialsTab] = React.useState<ToggleType[] | null>(null);
  const [toggleInvestmentProfileTab, setToggleInvestmentProfileTab] = React.useState<ToggleType[] | null>(null);
  const [cipInfo, setCipInfo] = React.useState<CIPInfo | undefined>(undefined);
  const runtimeConfig = useRuntimeConfig();
  const isPcmReportingEnabled = runtimeConfig.configData.featureFlags?.pcmReporting?.enabled;
  const isRiskConsiderationsEnabled = runtimeConfig.configData.featureFlags?.riskConsiderations?.enabled;
  const isClientReviewEnabled = runtimeConfig.configData.featureFlags?.clientReview?.enabled;
  const isRocEnabled = runtimeConfig.configData.featureFlags?.rocEnabled?.enabled;
  const basePcdV2Url = `/business-relation/${brIdFromUrl}`;
  const [pcdV2DistributionUrl, setPcdV2DistributionUrl] = React.useState<string>(
    `${basePcdV2Url}/distributions-overview`,
  );
  const urlWithDocumentId = urlSearchDocumentId ? `?documentId=${urlSearchDocumentId}` : '';
  const [pcdV2DocOverviewUrl, setPcdV2DocOverviewUrl] = React.useState<string>(
    `${basePcdV2Url}/documents-overview${urlWithDocumentId}`,
  );

  const accountNumber = useSelector((state: GlobalState) => state.caseInformation.accountInformation?.accountNumber);
  const accountNames = useSelector((state: GlobalState) => state.caseInformation.accountInformation?.accountName);
  const brTypeFromStore = useSelector((state: GlobalState) => state.caseInformation.accountInformation?.brType);
  const brSubTypeFromStore = useSelector((state: GlobalState) => state.caseInformation.accountInformation?.brSubType);
  const caseId = useSelector((state: GlobalState) => state.caseInformation.caseId);
  const clickRefresh = useSelector((state: GlobalState) => state.notifications.clickRefresh);
  const tabRef = useRef(selectedTab);
  const tabsNeedingSaveValidation: BrTabItems[] = [
    BrTabItems.CORE_DATA,
    BrTabItems.BR_RELATIONS,
    BrTabItems.BR_SERVICING,
    BrTabItems.BR_DELIVERY_INSTRUCTIONS,
    BrTabItems.BR_CONTACT_NOTES,
    BrTabItems.BR_PURPOSE,
    BrTabItems.INVESTMENT_PROFILE,
    BrTabItems.FINANCIALS,
    BrTabItems.RISK_CONSIDERATIONS,
  ];
  const { getFinancialsFeatures, getInvestmentProfileFeatures } = featureService;
  const isFinancialsEnable = findConfigFlagByCode(NNM_ENABLED_FLAG, toggleFinancialsTab);
  const isInvestmentProfileEnable = findConfigFlagByCode(INVESTMENT_PROFILE_ENABLED_FLAG, toggleInvestmentProfileTab);
  const { postMessageToIFrameByType } = usePostMessageToIFrame();
  const brId = brIdFromStore || brIdFromUrl;
  const splitMasterNumber = getBrMasterNumberForDisplay(accountNumber);
  const isUatEnvironment = runtimeConfig.configData.env === 'pcm-uat1' || runtimeConfig.configData.env === 'pcm-uat2';
  const navigateToTab = (newTabId: BrTabItems) => {
    const iframe = getIFrame(shadowRoot);

    if (tabsNeedingSaveValidation.includes(selectedTab) && isNotNullOrUndefined(iframe)) {
      postMessageToIFrameByType(ClmPortalOutgoingPostMessageTypes.CHANGE_TAB_SAVE_CHECK);
      tabRef.current = newTabId;
    } else {
      tabRef.current = newTabId;
      changeTab(brIdFromStore, newTabId);
    }
  };

  const handleTabChange = (_event: SyntheticEvent, newValue: BrTabItems) => {
    navigateToTab(newValue);
  };

  const {
    data: brInfoRes,
    isLoading: isBrInfoLoading,
    error: isBrInfoError,
    refetch: refetchBrInfo,
  } = useFetchBr(brId);
  const isForbidden = isBrInfoError?.response?.status === 403 || brLockError?.response?.status === 403;

  const isClmBr = brInfoRes?.brStoredInClm ?? false;
  const { data: blockingInstructions } = useFetchBlockingInstructions(brIdFromUrl, isClmBr && isRocEnabled);
  const activeBlockingInstructions = blockingInstructions?.filter((instruction) => instruction.status === 'ACTIVE');
  const isBrBlocked = isNotNullOrUndefined(activeBlockingInstructions) && activeBlockingInstructions.length > 0;
  const isBlockedSecret = activeBlockingInstructions?.some((instruction) => instruction.disclosureType === 'SECRET');

  useEffect(() => {
    return () => {
      //Reset tab on unmount
      dispatch(setBrTab(BrTabItems.CORE_DATA));
    };
  }, []);

  const changeTab = (brId: string | undefined, selectedTabName: React.SetStateAction<any>, hash?: string) => {
    dispatch(setBrTab(selectedTabName));
    if (selectedTabName === BrTabItems.DOCUMENT_OVERVIEW && !!urlSearchDocumentId) {
      navigate(
        `/business-relation/edit/${brId}/${selectedTabName}?documentId=${urlSearchDocumentId}${
          brSource === 'CRM' ? '?brSource=CRM' : ''
        }`,
      );
    } else if (selectedTabName === BrTabItems.DISTRIBUTION && !!distributionRequestIdFromParam) {
      navigate(
        `/business-relation/edit/${brId}/${selectedTabName}/distribution/${distributionRequestIdFromParam}${
          brSource === 'CRM' ? '?brSource=CRM' : ''
        }`,
      );
    } else {
      navigate(
        `/business-relation/edit/${brId}/${selectedTabName}${brSource === 'CRM' ? '?brSource=CRM' : ''}${hash ?? ''}`,
      );
    }
  };

  useEffect(
    function setupPcdV2RoutingState() {
      if (!brIdFromUrl) {
        return;
      }
      if (distributionRequestIdFromParam) {
        setPcdV2DistributionUrl(`/distribution/${distributionRequestIdFromParam}`);
        return;
      }
      setPcdV2DistributionUrl(`/business-relation/${brIdFromUrl}/distributions-overview`);
      setPcdV2DocOverviewUrl(`/business-relation/${brIdFromUrl}/documents-overview${urlWithDocumentId}`);
    },
    [brIdFromUrl, distributionRequestIdFromParam, urlWithDocumentId],
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage, false);

    function handleMessage(event: any) {
      const { clearPcdUrl } = event.data;

      if (clearPcdUrl) {
        setPcdV2DistributionUrl(`${basePcdV2Url}/distributions-overview`);
      }

      const message = event.data.message;
      switch (message) {
        case ClmPortalIncomingPostMessageTypes.SET_ACCOUNT_DETAILS: {
          // message from br-frontend
          const { accountNr, accountName, brUid } = event.data.value;
          dispatch(
            setAccountCaseInformation({
              accountNumber: accountNr,
              accountName,
              brUid,
            }),
          );
          changeTab(brUid, BrTabItems.CORE_DATA);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_DOC_TAB_V2: {
          // message from pco-product-service-frontend
          if (brIdFromStore && userId) {
            const { distributionRequestId, initiator } = event.data.value;
            if (distributionRequestId) {
              setPcdV2DistributionUrl(`/distribution/${distributionRequestId}?initiator=${initiator}`);
            }
            changeTab(brIdFromStore, BrTabItems.DISTRIBUTION);
          }
          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_MSD: {
          // message from pco-product-service-frontend
          openInSameTab(`${runtimeConfig.configData.iframeUrls.msdIframe}/${event.data.value}`);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_TO_PARTNER: {
          const path = `/partner/edit/${event.data.value.partnerUid}/core-data?wizardBrUid=${event.data.value.wizardBrUid ?? ''}&wizardRole=${event.data.value.wizardRole ?? ''}`;
          if (isWena) {
            requestHostNavigation(path);
            break;
          }
          // message from pcm-br-frontend
          window.open(path);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_TO_TASK: {
          // message from pco-frontend when a task is created, and the user clicks the view task button
          dispatch(
            updateNotifications({
              currentTaskId: event.data.value.currentTaskId,
              clickRefresh: clickRefresh + 1,
            }),
          );
          window.open(
            `${runtimeConfig.configData.iframeUrls.partnerCoreIframe}/home/tasks/all-tasks?currentTaskId=${event.data.value.currentTaskId}&clickRefresh=${clickRefresh}`,
          );

          break;
        }
        case ClmPortalIncomingPostMessageTypes.SEE_ACCOUNT_LIST: {
          // message from pcm-investment-profile-frontend
          if (brIdFromStore) {
            changeTab(brIdFromStore, BrTabItems.CORE_DATA);
          }

          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_TO_RISK_CONSIDERATIONS: {
          window.open(`/business-relation/edit/${event.data.value.brUid}/risk-considerations`);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB_INVESTMENT_PROFILE: {
          changeTab(brIdFromStore, BrTabItems.INVESTMENT_PROFILE);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB_PRODUCTS_AND_SERVICES: {
          changeTab(brIdFromStore, BrTabItems.PRODUCTS_AND_SERVICES);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB_REPORTING: {
          changeTab(brIdFromStore, BrTabItems.REPORTING);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.NEW_TAB_BR_CORE_DATA: {
          const { brId, brSource } = event.data.value;
          const path = `${routeList.brCoreData(brId)}${brSource === 'CRM_CH_BR' ? '?brSource=CRM' : ''}`;

          if (isWena) {
            requestHostNavigation(path);
            break;
          }
          window.open(path);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.OPEN_NEW_BR_TAB: {
          const { brId, brTab, hash } = event.data.value;
          window.open(`/business-relation/edit/${brId}/${brTab}${isNotNullOrUndefined(hash) ? '#' + hash : ''}`);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.NEW_TAB_OPEN_DOCUMENT: {
          const { documentBlob } = event.data.value;
          const url = URL.createObjectURL(documentBlob);
          const pdfWindow = window.open();
          if (pdfWindow) {
            pdfWindow.location.href = url;
          }

          break;
        }
        case ClmPortalIncomingPostMessageTypes.SAVED_BR: {
          setIsBrSaved((prevState) => !prevState);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB: {
          changeTab(brIdFromUrl, tabRef.current);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.CHANGE_TAB_BR_SERVICING: {
          changeTab(brIdFromUrl, BrTabItems.BR_SERVICING);

          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_TO_BR: {
          const { brUid } = event.data.value;
          navigate(`/business-relation/edit/${brUid}/core-data`);
          break;
        }
        case ClmPortalIncomingPostMessageTypes.REDIRECT_TO_TOOLS: {
          window.open(`/home/tools`);
          break;
        }
        case ClmPortalIncomingPostMessageTypes.RELOAD_PAGE: {
          window.location.reload();
          break;
        }

        // No default
      }
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [accountNumber, userId, brIdFromUrl]);

  useEffect(() => {
    if (brId) {
      refetchBrInfo();
    }
  }, [isBrSaved, brIdFromStore, brIdFromUrl, refetchBrInfo, brId]);

  const navigationWizardMode = resolveNavigationWizardMode({
    isWena,
    isClmBr,
    brTypeFromStore,
    navigateToTab,
    selectedTab,
    featureFlags: runtimeConfig.configData.featureFlags,
  });

  const showNavigationWizard = navigationWizardMode.type !== 'hidden';

  const brStatus = brInfoRes?.brStoredInClm
    ? brInfoRes.businessRelationshipDto?.businessRelationshipLifecycle
    : brInfoRes?.accountRelationDetailsDto?.status;

  useEffect(() => {
    if (!brIdFromStore && !brIdFromUrl) {
      dispatch(
        setAccountCaseInformation({
          accountNumber: '',
          accountName: '',
          brUid: '',
        }),
      );
      navigate('/business-relation/edit');
    }
  }, [brIdFromStore, brIdFromUrl, dispatch, history]);

  useEffect(() => {
    if (brInfoRes) {
      if (brInfoRes.brStoredInClm) {
        if (brInfoRes.businessRelationshipDto) {
          const {
            uid,
            businessRelationshipName,
            businessRelationshipType,
            businessRelationshipMasterNumber,
            businessRelationshipSubtype,
          } = brInfoRes.businessRelationshipDto;
          dispatch(
            setAccountCaseInformation({
              brUid: uid,
              accountName: businessRelationshipName,
              accountNumber: businessRelationshipMasterNumber,
              brType: businessRelationshipType,
              brSubType: businessRelationshipSubtype,
            }),
          );
        }
      } else {
        dispatch(setAccountCaseInformation(brInfoRes.accountRelationDetailsDto));
      }
    }
  }, [brInfoRes, dispatch]);

  useEffect(() => {
    if (brId && isNotNullOrUndefined(brInfoRes)) {
      selectedTabFromUrl
        ? changeTab(brId, selectedTabFromUrl, location.hash ?? '')
        : changeTab(brId, BrTabItems.CORE_DATA);
    }
  }, [brId, brInfoRes, selectedTabFromUrl, location.hash]);

  useEffect(() => {
    if (isBrInfoError && isBrInfoError.response?.status !== 403) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            technicalInfo: {
              details: 'Error when fetching the business relation information from pcm-br-mgmt',
              title: 'Error when fetching the business relation information',
              userErrorMessage: 'An error occurred while fetching the business relation information',
            },
            httpError: isBrInfoError,
          },
        }),
      );
    }
  }, [isBrInfoError, dispatch]);

  useEffect(() => {
    const fetchFeatures = async () => {
      const [financialsResult, investmentResult] = await Promise.allSettled([
        getFinancialsFeatures(),
        getInvestmentProfileFeatures(),
      ]);

      setToggleFinancialsTab(financialsResult.status === 'fulfilled' ? financialsResult.value : []);
      setToggleInvestmentProfileTab(investmentResult.status === 'fulfilled' ? investmentResult.value : []);
    };

    fetchFeatures();

    return () => {
      dispatch(setCaseId(undefined));
      dispatch(setAccountCaseInformation(undefined));
    };
  }, []);

  useEffect(() => {
    if (brIdFromUrl) {
      investmentProfileService
        .getInvestmentProfileState(brIdFromUrl)
        .then((cip) => setCipInfo(cip))
        .catch((error) => {
          if (error.response?.status === 403) {
            setCipInfo(undefined);
          } else {
            throw error;
          }
        });
    }

    return () => setCipInfo(undefined);
  }, [brIdFromUrl]);

  const [localServiceAgreementUid, setLocalServiceAgreementUid] = useState<string>('');

  useEffect(() => {
    if (serviceAgreementUid) {
      setLocalServiceAgreementUid(serviceAgreementUid);
    }
  }, [serviceAgreementUid]);

  useEffect(() => {
    if (caseId) {
      setPcoUrl(`/accounts/${accountNumber}/case/${caseId}/update`);
    }
    if (localServiceAgreementUid && brIdFromUrl && !serviceRoleUid) {
      setPcoUrl(`/accounts/${brIdFromUrl}/serviceAgreement/${localServiceAgreementUid}/details`);
      return;
    }
    if (accountNumber && brIdFromUrl && !serviceRoleUid) {
      setPcoUrl(`/accounts?accountNumber=${accountNumber}&brUid=${brIdFromUrl}`);
      return;
    }
    if (brIdFromUrl && !localServiceAgreementUid && !serviceRoleUid) {
      setPcoUrl(`/accounts/${brIdFromUrl}`);
      return;
    }
    if (brIdFromStore && !localServiceAgreementUid && !serviceRoleUid && !brIdFromUrl) {
      setPcoUrl(`/accounts/${brIdFromStore}`);
      return;
    }
    if (brIdFromUrl && serviceRoleUid && localServiceAgreementUid) {
      setPcoUrl(
        `/accounts/${brIdFromUrl}/serviceAgreement/${localServiceAgreementUid}/details/serviceRole/${serviceRoleUid}/details`,
      );
      return;
    }
  }, [brIdFromStore, localServiceAgreementUid, serviceRoleUid, caseId, accountNumber, brIdFromUrl]);

  const { onClick: copyOnClick, feedbackMessage: copyToClipboardFeedbackMessage } = useCopyToClipboard();

  if (isForbidden) {
    return <ZfAccessDenied title={t('accessDenied.title')} subtitle={t('accessDenied.br.subtitle')} />;
  }

  return (
    <>
      <PageTitleToken
        alternateTitle={accountNumber ? (isUatEnvironment ? 'BR' : `BR: ${splitMasterNumber}`) : undefined}
      />
      {brIdFromStore ? (
        <TabContext value={selectedTab}>
          {!isWena && (
            <AppBar position="static" sx={partnerTabStyles.topBar} elevation={0}>
              <Grid container direction="row" sx={{ flexWrap: 'nowrap', columnGap: { xs: 2, lg: 4 } }}>
                <Grid item xs sx={{ minWidth: 0, overflow: 'hidden' }}>
                  <Box
                    display="flex"
                    sx={{ padding: 0, margin: 0, minHeight: '68px', gap: '10px', minWidth: 0, overflow: 'hidden' }}
                    alignItems="center"
                    mb={2}
                  >
                    {hasMultipleTabs && (
                      <MultiTabWarning
                        tooltipText={t('multipleTabs.warning', { type: t('multipleTabs.warning.businessRelation') })}
                      />
                    )}
                    <Typography
                      variant="h1"
                      color={JBColors.white}
                      sx={{
                        backgroundColor: JBColors.core.stone['80'],
                        paddingX: '8px',
                        borderRadius: '2px',
                      }}
                      data-testid="br-label-icon"
                    >
                      {t('br-label-icon')}
                    </Typography>
                    <Tooltip
                      arrow
                      title={
                        isNotNullOrUndefined(copyToClipboardFeedbackMessage)
                          ? copyToClipboardFeedbackMessage
                          : isNotNullOrUndefined(accountNumber)
                            ? `${splitMasterNumber} | ${accountNames}`
                            : `${t('topbar.br.replacement')} | ${accountNames}`
                      }
                      componentsProps={{
                        tooltip: {
                          'data-testid': 'br-title-tooltip',
                        } as any,
                      }}
                    >
                      <Box
                        display="flex"
                        sx={{
                          padding: 0,
                          margin: 0,
                          minHeight: '68px',
                          gap: '10px',
                          minWidth: 0,
                          overflow: 'hidden',
                        }}
                        alignItems="center"
                        mb={2}
                        data-testid="br-title"
                      >
                        <Typography
                          variant="h1"
                          color="primary"
                          sx={{ ...partnerTabStyles.title, ...partnerTabStyles.cursorPointer, flexShrink: 0 }}
                          data-testid="br-master-number-title"
                          onClick={() =>
                            copyOnClick(
                              isNotNullOrUndefined(accountNumber) ? accountNumber : t('topbar.br.replacement'),
                            )
                          }
                        >
                          {isNotNullOrUndefined(accountNumber) ? splitMasterNumber : t('topbar.br.replacement')}
                        </Typography>
                        <Typography variant="h1" color="primary" sx={{ flexShrink: 0 }}>
                          |
                        </Typography>
                        <Typography
                          variant="h1"
                          color="primary"
                          sx={{ ...partnerTabStyles.title, ...partnerTabStyles.cursorPointer, minWidth: 0 }}
                          onClick={() => copyOnClick(accountNames ?? '')}
                          data-testid="br-name-title"
                        >
                          {accountNames}
                        </Typography>
                      </Box>
                    </Tooltip>
                    {isBrBlocked && <BlockingInstructionTag isSecret={!!isBlockedSecret} />}
                    {brStatus === 'CLOSED' && (
                      <Typography
                        variant="h1"
                        color={JBColors.support.red[100]}
                        sx={{ ...partnerTabStyles.title, flexShrink: 0 }}
                      >
                        {t('tabs.menu.closed')}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                {isBrLocked && !isClmBr && (
                  <Grid
                    item
                    xs="auto"
                    display="flex"
                    alignContent="center"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={{ minWidth: 0, maxWidth: { xs: '220px', lg: '360px' } }}
                  >
                    <KycLockMessage />
                  </Grid>
                )}
                <Grid
                  item
                  container
                  xs="auto"
                  justifyContent="flex-end"
                  display="flex"
                  alignItems="center"
                  sx={{ position: 'relative', top: '30px', height: '20px', flexShrink: 0 }}
                >
                  <Box height="20px" gap={1} mt={-1} display="flex" alignItems="center">
                    <Button
                      sx={{ minWidth: '32px', padding: 0, mr: 0 }}
                      id="revision-history-button"
                      data-testid="revision-history-button"
                      onClick={() =>
                        dispatch(
                          openModal({
                            modalType: 'BrRevisionHistoryModal',
                            modalProps: {
                              techId: brIdFromUrl,
                            },
                          }),
                        )
                      }
                    >
                      <SvgIcon>
                        <HistoryIcon />
                      </SvgIcon>
                    </Button>
                    <JBButton
                      variant="contained"
                      color="primary"
                      id="client-setup-readiness-button"
                      data-testid="client-setup-readiness-button"
                      size="small"
                      onClick={() =>
                        dispatch(
                          openModal({
                            modalType: 'ClientReadinessModal',
                            modalProps: { brUid: brIdFromStore },
                          }),
                        )
                      }
                    >
                      <JBIcon iconName="view" fill={JBColors.white} />
                      <Box ml={1}> {t('accounts.tabs.readiness.buttonText', 'Client setup readiness')}</Box>
                    </JBButton>
                    {isClmBr &&
                      (brTypeFromStore === BrTypes.INTERMEDIARY_B2B || !isNullOrUndefined(brSubTypeFromStore)) && (
                        <AccountsMoreButton />
                      )}
                  </Box>
                </Grid>
              </Grid>
            </AppBar>
          )}

          {(!showNavigationWizard ||
            (navigationWizardMode.type !== 'full' && navigationWizardMode.type !== 'hidden')) && (
            <TwoLevelTabNavigation
              onTabChange={handleTabChange}
              selectedTab={selectedTab}
              secondLevelBackgroundColor={JBColors.core.stone[20]}
              tabItems={[
                {
                  value: BrTabItems.CORE_DATA,
                  label: t('accounts.tabs.coreData.title', 'Core data'),
                  category: BrFormCategories.GENERAL,
                },
                {
                  value: BrTabItems.BR_PURPOSE,
                  label: t('accounts.tabs.brPurpose.title', 'BR profile'),
                  category: BrFormCategories.PROFILE,
                },
                {
                  value: BrTabItems.INVESTMENT_PROFILE,
                  category: BrFormCategories.PROFILE,
                  icon:
                    cipInfo?.state === 'MISSING' ? (
                      <Warning
                        id="cip-missing-warning-icon"
                        data-testid="cip-missing-warning-icon"
                        className={classes.warningIconPlacement}
                      />
                    ) : undefined,
                  iconPosition: 'end',
                  disabled: cipInfo?.state === 'NOT_ALLOWED' || brTypeFromStore === BrTypes.INTERMEDIARY_B2B,
                  classes: {
                    //@ts-ignore
                    wrapper: classes.tabIconWrapper,
                    labelIcon: classes.tabIconLabelContainer,
                  },
                  label: (
                    <LightTooltip
                      id="investment-profile-tab-tooltip"
                      data-testid="investment-profile-tab-tooltip"
                      placement="right"
                      title={
                        cipInfo?.state === 'NOT_ALLOWED'
                          ? 'Inactive due to the selected product'
                          : brTypeFromStore === BrTypes.INTERMEDIARY_B2B
                            ? 'Inactive as BR type is Intermediary B2B'
                            : ''
                      }
                    >
                      <span>{t('accounts.tabs.investmentProfile.title', 'Investment profile')}</span>
                    </LightTooltip>
                  ),
                  'data-testid': 'investment-profile-tab',
                  hidden: !isInvestmentProfileEnable?.value,
                  sx: {
                    ...partnerTabStyles.disabledTab,
                  },
                },
                {
                  label: t('accounts.tabs.brRelations.title'),
                  value: BrTabItems.BR_RELATIONS,
                  hidden: !isClmBr,
                  category: BrFormCategories.RELATIONSHIPS,
                },
                {
                  label: t('accounts.tabs.brServicing.title'),
                  value: BrTabItems.BR_SERVICING,
                  hidden: !isClmBr,
                  category: BrFormCategories.RELATIONSHIPS,
                },
                {
                  label: t('accounts.tabs.financials.title'),
                  value: BrTabItems.FINANCIALS,
                  hidden: !isFinancialsEnable?.value,
                  category: BrFormCategories.PRODUCTS_AND_SERVICES,
                },
                {
                  label: t('accounts.tabs.contactNotes.title'),
                  value: BrTabItems.BR_CONTACT_NOTES,
                  category: BrFormCategories.INTERACTIONS,
                  hidden: runtimeConfig.configData.featureFlags?.contactNote?.enabled ? false : !isClmBr,
                },
                {
                  value: BrTabItems.DISTRIBUTION,
                  label: t('accounts.tabs.distribution.title', 'Distribution'),
                  category: BrFormCategories.DOCUMENTS,
                },
                {
                  value: BrTabItems.DOCUMENT_OVERVIEW,
                  label: t('accounts.tabs.documentOverview.title', 'Document overview'),
                  category: BrFormCategories.DOCUMENTS,
                },
                {
                  value: BrTabItems.DOC_COLLECTION,
                  label: t('accounts.tabs.doccollection.title', 'Doc collection'),
                  category: BrFormCategories.DOCUMENTS,
                  hidden: !isClmBr,
                },
                {
                  label: t('accounts.tabs.productServices.title'),
                  value: BrTabItems.PRODUCTS_AND_SERVICES,
                  category: BrFormCategories.PRODUCTS_AND_SERVICES,
                },
                {
                  label: t('accounts.tabs.reporting.title'),
                  value: BrTabItems.REPORTING,
                  hidden: !isPcmReportingEnabled || isClmBr,
                  category: BrFormCategories.REPORTING,
                },
                {
                  label: t('accounts.tabs.riskConsiderations.title'),
                  value: BrTabItems.RISK_CONSIDERATIONS,
                  hidden: !isRiskConsiderationsEnabled,
                  category: BrFormCategories.PROFILE,
                },
                {
                  label: t('accounts.tabs.clientReview.title'),
                  value: BrTabItems.CLIENT_REVIEW,
                  hidden: !isClientReviewEnabled,
                  category: BrFormCategories.PROFILE,
                },
                {
                  label: t('accounts.tabs.brDeliveryInstructions.title'),
                  value: BrTabItems.BR_DELIVERY_INSTRUCTIONS,
                  hidden: !runtimeConfig.configData.featureFlags?.brDeliveryInstructions?.enabled,
                  category: BrFormCategories.PROFILE,
                },
                {
                  label: t('accounts.tabs.blockingInstructions.title'),
                  value: BrTabItems.BLOCKING_INSTRUCTIONS,
                  hidden: !runtimeConfig.configData.featureFlags?.rocEnabled?.enabled,
                  category: BrFormCategories.PROFILE,
                },
              ]}
            />
          )}

          <BrTabsFrames
            pcoUrl={pcoUrl}
            pcdV2DistributionUrl={pcdV2DistributionUrl}
            pcdV2DocOverviewUrl={pcdV2DocOverviewUrl}
            navigationWizardMode={navigationWizardMode}
            urlHash={location.hash}
            onNavigate={navigateToTab}
          />
        </TabContext>
      ) : isBrInfoLoading ? (
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ width: 1, height: '100vh' }}>
          <CircularProgress />
        </Stack>
      ) : (
        <EmbeddedIFrame
          url={`${runtimeConfig.configData.iframeUrls.brCoreIframe}/business-relationship/search`}
          title="Business Relationship"
          id="business-relationship-iframe"
          height={`calc(100% - ${TOPBAR_HEIGHT}px)`}
        />
      )}
    </>
  );
};

export default BrTabs;
